import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Send, MessageSquare, Search, ArrowLeft, Plus, Mail, Megaphone, Users } from 'lucide-react';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';

export default function AdminMessages() {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeUserId, setActiveUserId] = useState(null);
  const [activeAccount, setActiveAccount] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  // Compose-new-message dialog
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeQuery, setComposeQuery] = useState('');
  const [composeResults, setComposeResults] = useState([]);
  const [composeSearching, setComposeSearching] = useState(false);

  // Broadcast dialog
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [audience, setAudience] = useState('all');
  const [audienceCount, setAudienceCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);

  const fetchConversations = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setLoadingList(true);
      const response = await api.get('/admin/messages');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      if (showSpinner) toast.error('Failed to load messages');
    } finally {
      if (showSpinner) setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations(true);
    const interval = setInterval(() => fetchConversations(false), 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const refreshThread = useCallback(async (userId) => {
    try {
      const response = await api.get(`/admin/messages/${userId}`);
      const incoming = response.data.messages || [];
      const hasUnread = incoming.some((m) => m.sender_role === 'user' && !m.read_by_admin);
      if (hasUnread) {
        await api.post(`/admin/messages/${userId}/read`);
        setThread(incoming.map((m) => (m.sender_role === 'user' ? { ...m, read_by_admin: true } : m)));
        fetchConversations(false);
      } else {
        setThread(incoming);
      }
    } catch (error) {
      console.error('Failed to refresh conversation:', error);
    }
  }, [fetchConversations]);

  // Poll the currently open thread for new messages.
  useEffect(() => {
    if (!activeUserId) return;
    const interval = setInterval(() => refreshThread(activeUserId), 12000);
    return () => clearInterval(interval);
  }, [activeUserId, refreshThread]);

  const openConversation = async (userId, account) => {
    setActiveUserId(userId);
    setActiveAccount(account || null);
    try {
      setLoadingThread(true);
      const response = await api.get(`/admin/messages/${userId}`);
      setThread(response.data.messages || []);
      if (response.data.account) setActiveAccount(response.data.account);
      await api.post(`/admin/messages/${userId}/read`);
      fetchConversations(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingThread(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text || !activeUserId) return;
    try {
      setSending(true);
      const response = await api.post(`/admin/messages/${activeUserId}`, { body: text });
      setThread((prev) => [...prev, response.data]);
      setBody('');
      fetchConversations(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleComposeSearch = async (e) => {
    e.preventDefault();
    if (!composeQuery.trim()) return;
    try {
      setComposeSearching(true);
      const response = await api.get('/admin/search', { params: { q: composeQuery } });
      setComposeResults(response.data.users || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setComposeSearching(false);
    }
  };

  const startConversationWith = (u) => {
    setComposeOpen(false);
    setComposeQuery('');
    setComposeResults([]);
    openConversation(u.id, { id: u.id, email: u.email, name: u.name });
  };

  const fetchAudienceCount = useCallback(async (aud) => {
    try {
      setCountLoading(true);
      const response = await api.get('/admin/messages/audience/count', { params: { audience: aud } });
      setAudienceCount(response.data.count ?? 0);
    } catch (error) {
      console.error('Failed to load audience count:', error);
      setAudienceCount(null);
    } finally {
      setCountLoading(false);
    }
  }, []);

  useEffect(() => {
    if (broadcastOpen) {
      fetchAudienceCount(audience);
    }
  }, [broadcastOpen, audience, fetchAudienceCount]);

  const handleBroadcast = async (e) => {
    e.preventDefault();
    const text = broadcastBody.trim();
    if (!text) return;
    try {
      setBroadcasting(true);
      const response = await api.post('/admin/messages/broadcast', { body: text, audience });
      const sent = response.data.sent ?? 0;
      toast.success(`Message sent to ${sent} user${sent === 1 ? '' : 's'}`);
      setBroadcastOpen(false);
      setBroadcastBody('');
      setAudience('all');
      fetchConversations(false);
    } catch (error) {
      console.error('Failed to broadcast message:', error);
      toast.error('Failed to send broadcast');
    } finally {
      setBroadcasting(false);
    }
  };

  const audienceOptions = [
    { value: 'all', label: 'All users', description: 'Everyone with an account' },
    { value: 'not_used', label: "Haven't used minutes", description: 'Users who never processed a track' },
    { value: 'purchased', label: 'Purchased a package', description: 'Users who bought minutes' },
  ];

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const accountLabel = (acc) => {
    if (!acc) return 'User';
    return acc.name || acc.email || acc.id;
  };

  // Unread conversations first, then most recent.
  const sortedConversations = useMemo(() => {
    return [...conversations].sort((a, b) => {
      const au = (a.unread || 0) > 0 ? 1 : 0;
      const bu = (b.unread || 0) > 0 ? 1 : 0;
      if (au !== bu) return bu - au;
      return new Date(b.last_created_at || 0) - new Date(a.last_created_at || 0);
    });
  }, [conversations]);

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unread || 0), 0),
    [conversations]
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Messages
              {totalUnread > 0 && (
                <span className="px-2 py-0.5 text-[11px] rounded-full bg-red-500 text-white font-semibold">
                  {totalUnread} new
                </span>
              )}
            </CardTitle>
            <CardDescription>Read and reply to all user conversations</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Dialog.Root open={broadcastOpen} onOpenChange={(open) => {
              setBroadcastOpen(open);
              if (!open) { setBroadcastBody(''); setAudience('all'); setAudienceCount(null); }
            }}>
              <Dialog.Trigger asChild>
                <Button size="sm" variant="outline">
                  <Megaphone className="h-4 w-4 mr-1" />
                  Broadcast
                </Button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
                <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background rounded-lg p-6 w-full max-w-md z-50 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold mb-1 flex items-center gap-2">
                    <Megaphone className="h-5 w-5" />
                    Send to Many
                  </Dialog.Title>
                  <Dialog.Description className="text-sm text-muted-foreground mb-4">
                    Send one message to a whole group of users at once.
                  </Dialog.Description>
                  <form onSubmit={handleBroadcast}>
                    <label className="text-sm font-medium mb-2 block">Audience</label>
                    <div className="space-y-2 mb-4">
                      {audienceOptions.map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${audience === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
                        >
                          <input
                            type="radio"
                            name="audience"
                            value={opt.value}
                            checked={audience === opt.value}
                            onChange={() => setAudience(opt.value)}
                            className="mt-1"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{opt.label}</p>
                            <p className="text-xs text-muted-foreground">{opt.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {countLoading ? (
                        <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Counting recipients...</span>
                      ) : audienceCount === null ? (
                        <span>Recipient count unavailable</span>
                      ) : (
                        <span>This will reach <span className="font-semibold text-foreground">{audienceCount}</span> user{audienceCount === 1 ? '' : 's'}</span>
                      )}
                    </div>
                    <Textarea
                      placeholder="Type your message..."
                      value={broadcastBody}
                      onChange={(e) => setBroadcastBody(e.target.value)}
                      rows={4}
                      className="resize-none mb-4"
                      disabled={broadcasting}
                    />
                    <div className="flex justify-end gap-2">
                      <Dialog.Close asChild>
                        <Button type="button" variant="ghost" disabled={broadcasting}>Cancel</Button>
                      </Dialog.Close>
                      <Button type="submit" disabled={broadcasting || !broadcastBody.trim() || !audienceCount}>
                        {broadcasting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                        Send{audienceCount ? ` to ${audienceCount}` : ''}
                      </Button>
                    </div>
                  </form>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
            <Dialog.Root open={composeOpen} onOpenChange={(open) => {
              setComposeOpen(open);
              if (!open) { setComposeQuery(''); setComposeResults([]); }
            }}>
              <Dialog.Trigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  New Message
                </Button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
                <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background rounded-lg p-6 w-full max-w-md z-50 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold mb-1">New Message</Dialog.Title>
                  <Dialog.Description className="text-sm text-muted-foreground mb-4">
                    Search for a user to start or continue a conversation.
                  </Dialog.Description>
                  <form onSubmit={handleComposeSearch} className="flex gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email or name..."
                        value={composeQuery}
                        onChange={(e) => setComposeQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Button type="submit" disabled={composeSearching}>
                      {composeSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                    </Button>
                  </form>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {composeResults.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No results yet</p>
                    ) : (
                      composeResults.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => startConversationWith(u)}
                          className="w-full text-left px-3 py-2 rounded-md hover:bg-muted flex items-center gap-2"
                        >
                          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{u.email || 'No email'}</p>
                            {u.name && <p className="text-xs text-muted-foreground truncate">{u.name}</p>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  <Dialog.Close asChild>
                    <Button variant="ghost" className="absolute right-4 top-4" size="sm">&times;</Button>
                  </Dialog.Close>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex h-[60vh] rounded-md border overflow-hidden">
          {/* Left column: recipients / conversations */}
          <div className={`w-full md:w-80 md:border-r flex-col ${activeUserId ? 'hidden md:flex' : 'flex'}`}>
            <div className="px-3 py-2 border-b bg-muted/30 text-xs font-medium text-muted-foreground flex items-center justify-between">
              <span>Recipients</span>
              {totalUnread > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-semibold">
                  {totalUnread} new
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingList ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : sortedConversations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground px-4">
                  <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {sortedConversations.map((c) => {
                    const isActive = activeUserId === c.user_id;
                    const hasUnread = (c.unread || 0) > 0;
                    return (
                      <button
                        key={c.user_id}
                        onClick={() => openConversation(c.user_id, { id: c.user_id, email: c.email, name: c.name })}
                        className={`w-full text-left px-3 py-3 flex items-center gap-3 transition-colors ${isActive ? 'bg-muted' : 'hover:bg-muted/50'} ${hasUnread ? 'bg-red-500/5' : ''}`}
                      >
                        <div className="relative shrink-0">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <Mail className="h-4 w-4 text-primary" />
                          </div>
                          {hasUnread && (
                            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-background" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm truncate ${hasUnread ? 'font-semibold' : 'font-medium'}`}>
                              {c.name || c.email || c.user_id}
                            </p>
                            {hasUnread && (
                              <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-semibold shrink-0">
                                {c.unread}
                              </span>
                            )}
                          </div>
                          <p className={`text-xs truncate ${hasUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {c.last_sender_role === 'admin' ? 'You: ' : ''}{c.last_message}
                          </p>
                        </div>
                        <span className="text-[11px] text-muted-foreground shrink-0">{formatTime(c.last_created_at)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right column: active thread */}
          <div className={`flex-1 flex-col ${activeUserId ? 'flex' : 'hidden md:flex'}`}>
            {!activeUserId ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground px-4">
                <MessageSquare className="h-10 w-10 mb-3 opacity-40" />
                <p className="font-medium">Select a recipient</p>
                <p className="text-sm">Choose a conversation from the left to read and reply.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 px-4 py-3 border-b">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 md:hidden" onClick={() => { setActiveUserId(null); setThread([]); setActiveAccount(null); fetchConversations(false); }}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{accountLabel(activeAccount)}</p>
                    {activeAccount?.email && (
                      <p className="text-xs text-muted-foreground truncate">{activeAccount.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loadingThread ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : thread.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                      <MessageSquare className="h-10 w-10 mb-3 opacity-40" />
                      <p className="font-medium">No messages yet</p>
                      <p className="text-sm">Send the first message to start the conversation.</p>
                    </div>
                  ) : (
                    thread.map((m) => {
                      const isAdmin = m.sender_role === 'admin';
                      return (
                        <div key={m.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isAdmin ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                            <p className="text-xs font-semibold mb-0.5 opacity-80">
                              {isAdmin ? (m.sender_name || 'Admin') : (m.sender_name || 'User')}
                            </p>
                            <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                            <p className={`text-[10px] mt-1 ${isAdmin ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                              {formatTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={handleSend} className="flex gap-2 items-end border-t p-4">
                  <Textarea
                    placeholder="Type a reply..."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    rows={2}
                    className="resize-none"
                    disabled={sending}
                  />
                  <Button type="submit" disabled={sending || !body.trim()}>
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
